/**
 * Assistant Dashboard — the Administration Assistant's view (Wouter).
 *
 * Two main areas:
 * A. Daily Welcome Panel (greeting, quote, streaks, bonus tracker) — the
 *    role-specific TOP section.
 * B. The shared pipeline board, identical to the Adviser's and the New
 *    Business Assistant's — same six columns, same cards (CLAUDE.md §30).
 *    Wouter sees preparation requests and review calendar cards alongside
 *    his file folders so he has the team's full picture, but he cannot open
 *    or act on either: those cards are rendered inert by SharedPipeline and
 *    PrepRequestDetail refuses his active role on a deep link. That access
 *    gating is unchanged.
 *
 * Plus achievement celebration modals.
 *
 * Stats contract (§10 / §21.1 / §27.6): the stat cards, BonusTracker,
 * streaks and achievements are computed from `files` alone — prep rows are
 * fetched for board DISPLAY only and never reach a statistic.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target, Filter, Notebook,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { normalizePipeline } from '../services/pipelineService';
import QuoteCard from '../components/dashboard/QuoteCard';
import StatsBar from '../components/dashboard/StatsBar';
import SharedPipeline from '../components/dashboard/SharedPipeline';
import { getAllFiles } from '../api/filesApi';
import { getAllPrepRequests } from '../api/prepRequestsApi';
import { getActiveReviewClients, getReviewGroups } from '../api/reviewClientsApi';
import { getAllReferrals } from '../api/referralsApi';
import { getNoteCountsByFile, getAllSystemNotes } from '../api/fileNotesApi';
import { getNoteCountsByPrep } from '../api/prepNotesApi';
import { getFlaggedCountsByFile } from '../api/caseScreenshotsApi';
import { getGreeting, getCurrentMonthYear } from '../utils/dateHelpers';
import { computeMonthStats } from '../utils/dashboardStats';
import { getRandomQuote } from '../data/motivationalQuotes';
import useAchievements from '../hooks/useAchievements';
import SLAWarningToast from '../components/notifications/SLAWarningToast';
import DashboardReturnToast, { useDashboardReturn } from '../components/shared/DashboardReturnToast';
import BonusTracker from '../components/dashboard/BonusTracker';
import StreaksDisplay from '../components/gamification/StreaksDisplay';
import { StatusChangeAnimation } from '../components/gamification/Celebrations';
import { getSettings } from '../api/settingsApi';
import useAutoRefresh from '../hooks/useAutoRefresh';

const EMAIL_TO_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
};

function getFirstName(email) {
  return EMAIL_TO_NAME[(email || '').toLowerCase()] || 'there';
}

export default function AssistantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  // Preparation requests are a SEPARATE record type — loaded for pipeline
  // DISPLAY only, exactly as on the other two boards (CLAUDE.md §30: one
  // identical board for every role). They never feed file stats or the bonus
  // counter, which are computed from `files` alone (§10 / §21.1 / §27.6).
  const [prepRequests, setPrepRequests] = useState([]);
  // Referral cards — display only on this board (Adviser-only to open).
  // Never fed to file stats or the bonus counter.
  const [referrals, setReferrals] = useState([]);
  const [noteCounts, setNoteCounts] = useState({});
  // Per-file count of notes still awaiting acknowledgement — drives card
  // illumination (CLAUDE.md §31).
  // Role-keyed pending-acknowledgement counts (fileId → {role: n}). Scoped to
  // the viewer's active role inside SharedPipeline (owner-only illumination,
  // §31.4).
  const [pendingNoteCountsByRole, setPendingNoteCountsByRole] = useState({});
  // Prep-request note counts (total + role-keyed pending) — a separate
  // store from file notes; drives prep-card badges + illumination (§31).
  const [prepNoteCounts, setPrepNoteCounts] = useState({});
  const [prepPendingNoteCountsByRole, setPrepPendingNoteCountsByRole] = useState({});
  // System-event notes grouped by file id. Drives the bonus / SLA clock
  // and all six stat cards (see CLAUDE.md §21).
  const [systemNotesByFile, setSystemNotesByFile] = useState({});
  // Map of files.id → number of unresolved Layer 3 verification flags.
  // Drives the small amber dot on signed-stage kanban cards.
  const [flaggedCounts, setFlaggedCounts] = useState({});
  // Review calendar cards (§34.9/§34.10) — display-only on this board.
  const [reviewClients, setReviewClients] = useState([]);
  const [reviewGroupLabels, setReviewGroupLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(getCurrentMonthYear());
  const [quote] = useState(() => getRandomQuote());
  const [statusAnim, setStatusAnim] = useState(null);

  // `silent: true` re-runs the exact same queries WITHOUT toggling the
  // loading skeleton — used by the background auto-refresh so data updates
  // in place with no visual interruption.
  const loadFiles = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [data, noteData, flags, sysNotes, preps, prepNoteData, reviews, reviewGroups, referralRows] = await Promise.all([
        getAllFiles(),
        getNoteCountsByFile().catch(() => ({})),
        getFlaggedCountsByFile().catch(() => ({})),
        getAllSystemNotes().catch(() => ({})),
        // Prep sheets — DISPLAY ONLY on this board: the Administration
        // Assistant sees them but cannot open or work one (§30).
        getAllPrepRequests().catch(() => []),
        getNoteCountsByPrep().catch(() => ({})),
        // Review calendar cards (§34.9/§34.10) — DISPLAY ONLY on this
        // board: visible to every role, openable by the Adviser alone.
        // Never fed to stats, the bonus counter, SLA, or concurrency.
        getActiveReviewClients().catch(() => []),
        getReviewGroups().catch(() => ({})),
        // Referral cards — DISPLAY ONLY on this board: visible to every
        // role, openable by the Adviser alone (a later step wires the
        // detail view). Never fed to stats, the bonus counter, SLA or
        // concurrency. A missing table degrades to no referral cards.
        getAllReferrals().catch(() => []),
      ]);
      const { counts = {}, pendingByRole = {} } = noteData || {};
      const { counts: prepCounts = {}, pendingByRole: prepPendingByRole = {} } = prepNoteData || {};
      setFiles(data);
      setNoteCounts(counts);
      setPendingNoteCountsByRole(pendingByRole);
      setPrepNoteCounts(prepCounts);
      setPrepPendingNoteCountsByRole(prepPendingByRole);
      setFlaggedCounts(flags);
      setSystemNotesByFile(sysNotes);
      setPrepRequests(preps);
      setReviewClients(reviews);
      setReviewGroupLabels(reviewGroups);
      setReferrals(referralRows);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Return-to-dashboard (CLAUDE.md §39): toast + card highlight carried in
  // location.state by src/utils/returnToDashboard.js.
  const { toast: returnToast } = useDashboardReturn({ ready: !loading });

  // Silent auto-refresh: refetch when the tab regains visibility, plus
  // background polling every 45s while visible. Reuses loadFiles in
  // silent mode — state updates in place, no skeleton, no scroll reset.
  useAutoRefresh(() => loadFiles({ silent: true }));

  // Refresh note counts after the login catch-up gate (§31.9) acknowledges
  // notes, so cards stop flashing without a manual reload.
  useEffect(() => {
    const onAck = () => loadFiles();
    window.addEventListener('inbox:notes-acknowledged', onAck);
    return () => window.removeEventListener('inbox:notes-acknowledged', onAck);
  }, [loadFiles]);

  // First-load normalize: bring the pipeline into compliance with the
  // strict In Progress invariants (CLAUDE.md §7.6) — runs once per
  // dashboard mount, then refetches if anything changed.
  const normalizedRef = useRef(false);
  useEffect(() => {
    if (normalizedRef.current) return;
    if (loading) return;
    if (!files || files.length === 0) return;
    normalizedRef.current = true;
    (async () => {
      try {
        const applied = await normalizePipeline(files);
        if (applied > 0) {
          console.warn(`[pipeline-normalize] applied ${applied} op(s) — refetching files`);
          await loadFiles();
        }
      } catch (e) {
        console.warn('[pipeline-normalize] failed:', e);
      }
    })();
  }, [files, loading, loadFiles]);

  // Achievements hook
  const { newlyEarned, clearNewlyEarned, stats: achievementStats } = useAchievements(files, user?.email);

  // File stats for the BonusTracker — same computeMonthStats result the
  // statistics bar's File folders "Completed" tile shows (§10 / §21.1).
  const stats = computeMonthStats(files, filterMonth, systemNotesByFile);

  // BonusTracker counts every file completed in the selected month —
  // same filter as the File Completed column and the "Completed" stat
  // card above. Derived from the existing `stats` object so the two
  // numbers cannot drift apart (CLAUDE.md §10, §21.1).
  const qualifyingCount = stats.completed;

  // Streaks
  const processingStreak = achievementStats.consecutiveWithinSLA || 0;

  // Load settings from Supabase for bonus amounts
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getSettings().then((data) => setSettings(data)).catch(() => {});
  }, []);

  const firstName = getFirstName(user?.email);

  return (
    <div style={styles.container}>
      {/* Return-to-dashboard toast (§39) */}
      <DashboardReturnToast message={returnToast} />

      {/* A. Welcome Panel */}
      <motion.div
        className="card"
        style={styles.welcomePanel}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="welcome-top" style={styles.welcomeTop}>
          <div style={styles.welcomeLeft}>
            <h2 style={styles.welcomeGreeting}>{getGreeting(firstName)} 👋</h2>
            <div style={styles.quoteWrap}>
              <QuoteCard quote={quote} />
            </div>
            {/* The Administration Assistant can load preparation requests
                himself (CLAUDE.md 26.1) — e.g. when working a client file
                surfaces documents the New Business Assistant must prepare.
                Same form and flow as the Adviser's, minus the Adviser-only
                test checkbox. */}
            <button
              className="btn btn-secondary"
              style={{ marginTop: '0.75rem' }}
              onClick={() => navigate('/submit-prep')}
            >
              <Notebook size={16} /> Submit New Preparation Request
            </button>
          </div>
          <StreaksDisplay
            processingStreak={processingStreak}
            weeklyStreak={0}
          />
        </div>

        {/* Enhanced Bonus Tracker */}
        <BonusTracker
          qualifyingCount={qualifyingCount}
          settings={settings}
        />
      </motion.div>

      {/* Month Filter — drives the stat cards and the File Completed column */}
      <div style={styles.filterRow}>
        <Filter size={16} color="#6B7A8D" />
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={styles.monthInput}
        />
      </div>

      {/* Unified statistics bar (CLAUDE.md §37) — fixed "File folders" set
          for the Administration Assistant: same calculations and numbers as
          the Adviser's File folders view. */}
      <StatsBar
        files={files}
        systemNotesByFile={systemNotesByFile}
        filterMonth={filterMonth}
      />

      {/* Pipeline Overview — file folders only (role-tailored, §30) */}
      <div>
        <h3 style={styles.sectionTitle}>
          <Target size={16} /> Pipeline Overview
        </h3>
        <SharedPipeline
          files={files}
          prepRequests={prepRequests}
          reviewClients={reviewClients}
          reviewGroupLabels={reviewGroupLabels}
          referrals={referrals}
          noteCounts={noteCounts}
          pendingNoteCountsByRole={pendingNoteCountsByRole}
          prepNoteCounts={prepNoteCounts}
          prepPendingNoteCountsByRole={prepPendingNoteCountsByRole}
          flaggedCounts={flaggedCounts}
          filterMonth={filterMonth}
          onFileClick={(f) => navigate(`/file/${f.id}`)}
        />
      </div>

      {/* Achievement Celebration Modal */}
      {newlyEarned.length > 0 && (
        <div style={styles.celebrationOverlay} onClick={clearNewlyEarned}>
          <motion.div
            style={styles.celebrationCard}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
            data-celebration="badge"
          >
            <motion.span
              style={styles.celebrationEmoji}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 150, delay: 0.2 }}
            >
              {newlyEarned[0].emoji}
            </motion.span>
            <h2 style={styles.celebrationTitle}>Achievement Unlocked!</h2>
            <h3 style={styles.celebrationName}>{newlyEarned[0].name}</h3>
            <p style={styles.celebrationDesc}>{newlyEarned[0].description}</p>
            <button className="btn btn-primary" onClick={clearNewlyEarned} style={{ marginTop: '1rem' }}>
              Awesome!
            </button>
          </motion.div>
        </div>
      )}

      {/* Status Change Animation */}
      <StatusChangeAnimation
        show={!!statusAnim}
        status={statusAnim}
        onComplete={() => setStatusAnim(null)}
      />

      {/* SLA Warning Toasts */}
      <SLAWarningToast files={files} />
    </div>
  );
}

const styles = {
  container: {},
  welcomePanel: {
    marginBottom: '1.5rem',
    background: 'linear-gradient(135deg, rgba(15, 76, 117, 0.03), rgba(27, 154, 170, 0.06))',
    border: '1px solid rgba(27, 154, 170, 0.1)',
  },
  welcomeTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  welcomeLeft: {
    flex: 1,
    minWidth: '250px',
  },
  welcomeGreeting: {
    fontSize: '1.3rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.5rem',
  },
  quoteWrap: {
    maxWidth: '500px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.75rem',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  monthInput: {
    padding: '0.35rem 0.6rem',
    border: '1px solid #D0DAE6',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    width: 'auto',
  },
  celebrationOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '1rem',
  },
  celebrationCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '2.5rem',
    textAlign: 'center',
    maxWidth: '380px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  celebrationEmoji: {
    display: 'inline-block',
    fontSize: '4rem',
    marginBottom: '0.5rem',
  },
  celebrationTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--cp-accent)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.25rem',
  },
  celebrationName: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
    marginBottom: '0.35rem',
  },
  celebrationDesc: {
    fontSize: '0.9rem',
    color: '#6B7A8D',
  },
  pipeline: {
    display: 'flex',
    gap: '0.75rem',
    overflowX: 'auto',
    paddingBottom: '1rem',
    alignItems: 'flex-start',
  },
  pipelineColumn: {
    minWidth: '180px',
    flex: '1 1 180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    verticalAlign: 'top',
  },
  pipelineColumnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '0.75rem',
    padding: '0.5rem 0',
    height: '48px',
    minHeight: '48px',
  },
  pipelineColumnLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    flex: 1,
  },
  pipelineColumnCount: {
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '0.1rem 0.4rem',
    borderRadius: '999px',
  },
  pipelineColumnCards: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: '0.5rem',
  },
  pipelineEmptyColumn: {
    fontSize: '0.8rem',
    color: '#6B7A8D',
    textAlign: 'center',
    padding: '2rem 0',
    fontStyle: 'italic',
  },
};
